import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Chip,
  CircularProgress
} from '@mui/material';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../Configuration';
import { isUrduText } from '../utils/urduFont';

const AddTestDialog = ({ open, onClose }) => {
  const [testName, setTestName] = useState('');
  const [courseId, setCourseId] = useState('');
  const [date, setDate] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]); // Store available campuses
  const [selectedCampus, setSelectedCampus] = useState(''); // Selected campus for test
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCampuses = async () => {
      const campusCollection = collection(db, 'Campus');
      const campusSnapshot = await getDocs(campusCollection);
      let campusList = campusSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter campuses based on user's campusCode from localStorage
      const allowedCampus = JSON.parse(localStorage.getItem('campusCode')) || [];
      if (allowedCampus.length > 0) {
        campusList = campusList.filter(campus => allowedCampus.includes(campus.campusCode));
      }

      setCampuses(campusList);
    };

    fetchCampuses();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      const courseCollection = collection(db, 'Courses');
      const courseSnapshot = await getDocs(courseCollection);
      let courseList = courseSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Filter courses by selected campus if campus is selected
      if (selectedCampus) {
        courseList = courseList.filter(course => 
          course.campusCodes && course.campusCodes.includes(selectedCampus)
        );
      }
      
      setCourses(courseList);
    };
    
    fetchCourses();
  }, [selectedCampus]);

  // Auto-select campus if only one is available
  useEffect(() => {
    if (campuses.length === 1 && !selectedCampus) {
      setSelectedCampus(campuses[0].campusCode);
    }
  }, [campuses, selectedCampus]);

  const handleAddTest = async () => {
    if (!testName || !courseId || !date || !maxScore || !selectedCampus) {
      alert('Please fill in all fields.');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'Tests'), {
        testName,
        courseId,
        date,
        maxScore: parseFloat(maxScore),
        campusCodes: [selectedCampus], // Store selected campus as an array for compatibility
      });
      onClose();
    } catch (error) {
      console.error('Error adding test: ', error);
      alert('Failed to add test.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTestName('');
      setCourseId('');
      setDate('');
      setMaxScore('');
      setSelectedCampus('');
    }
  }, [open]);

  // Auto-select campus when dialog opens and campuses are loaded
  useEffect(() => {
    if (open && campuses.length === 1 && !selectedCampus) {
      setSelectedCampus(campuses[0].campusCode);
    }
  }, [open, campuses, selectedCampus]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add New Test</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Test Name"
          type="text"
          fullWidth
          value={testName}
          onChange={(e) => setTestName(e.target.value)}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: isUrduText(testName)
                ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                : 'inherit',
              direction: 'ltr',
              textAlign: 'left'
            }
          }}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Course</InputLabel>
          <Select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
          >
            {courses.map((course) => (
              <MenuItem key={course.id} value={course.id}>
                {course?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          margin="dense"
          label="Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />

        <TextField
          margin="dense"
          label="Max Score"
          type="number"
          fullWidth
          value={maxScore}
          onChange={(e) => setMaxScore(e.target.value)}
        />

        {/* Campus Selection */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Select Campus</InputLabel>
          <Select
            value={selectedCampus}
            onChange={(e) => setSelectedCampus(e.target.value)}
            input={<OutlinedInput label="Select Campus" />}
            disabled={campuses.length === 1}
          >
            {campuses.map((campus) => (
              <MenuItem key={campus.id} value={campus.campusCode}>
                {campus.campusName} ({campus.campusCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleAddTest} 
          color="primary" 
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTestDialog;
